import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Upload, Eye, Trash2, Loader2, Search, FileCheck2 } from 'lucide-react';

/**
 * Player Documents ▸ Registration paperwork (docs/AD_DASHBOARD_AND_FINANCE.md §3).
 * Per registration: every family → each player's waivers (the form's agreements,
 * signed or missing) and birth certificate (the family's upload from the form, or
 * one the AD uploads here). Files are private (GridFS `reg_uploads`), opened through
 * the registration download route. Condensed: one strip, the list scrolls in-panel.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;
const inputCls = 'rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-2 py-1.5 text-sm';

export default function PlayerDocumentsSummary({ orgId }) {
  const [d, setD] = useState(null);
  const [formId, setFormId] = useState('');
  const [q, setQ] = useState('');
  const [missingOnly, setMissingOnly] = useState(false);
  const [busy, setBusy] = useState('');
  const fileRef = useRef(null);
  const target = useRef(null);
  const base = `${API_URL}/api/player-documents/${orgId}`;

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${base}/summary`, { params: formId ? { form_id: formId } : {} }); setD(data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not load player documents'); setD({ families: [], forms: [], totals: {} }); }
  }, [base, formId]);
  useEffect(() => { load(); }, [load]);

  const fams = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (d?.families || []).filter((f) => (!missingOnly || !f.complete)
      && (!s || `${f.family} ${f.players.map((p) => `${p.name} ${p.team}`).join(' ')}`.toLowerCase().includes(s)));
  }, [d, q, missingOnly]);

  const pickFile = (pid) => { target.current = pid; fileRef.current?.click(); };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !target.current) return;
    const fd = new FormData(); fd.append('file', file);
    setBusy(target.current);
    try { await axios.post(`${base}/birth-certificate/${target.current}`, fd); toast.success('Birth certificate stored'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); } finally { setBusy(''); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Remove the birth certificate uploaded for ${p.name}?`)) return;
    setBusy(p.id);
    try { await axios.delete(`${base}/birth-certificate/${p.id}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not remove'); } finally { setBusy(''); }
  };
  const t = d?.totals || {};
  const fileUrl = (fid) => `${API_URL}/api/reg-submissions/${orgId}/uploads/${fid}`;

  return (
    <div className="space-y-3" data-testid="player-documents-summary">
      <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} />
      <div className="flex flex-wrap items-center gap-2">
        <select value={formId || d?.form_id || ''} onChange={(e) => setFormId(e.target.value)} className={`${inputCls} max-w-sm`} data-testid="pd-form">
          {(d?.forms || []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2 top-2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Family, player, team…" className={`${inputCls} pl-7 w-56`} />
        </div>
        <label className="text-sm flex items-center gap-1 text-slate-700 dark:text-slate-300">
          <input type="checkbox" checked={missingOnly} onChange={(e) => setMissingOnly(e.target.checked)} data-testid="pd-missing-only" /> Missing only
        </label>
        <div className="ml-auto flex flex-wrap gap-2 text-xs" data-testid="pd-totals">
          {[['Families complete', `${t.families_complete ?? 0} / ${t.families ?? 0}`, 'text-emerald-600 dark:text-emerald-400'],
            ['Players', t.players ?? 0, 'text-slate-900 dark:text-white'],
            ['Waivers missing', t.waivers_missing ?? 0, 'text-red-600 dark:text-red-400'],
            ['Birth certs missing', t.bc_missing ?? 0, 'text-amber-600 dark:text-amber-400']].map(([l, v, c]) => (
            <span key={l} className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 px-2.5 py-1">
              <span className="text-slate-500 dark:text-slate-400">{l}</span> <b className={`tabular-nums ${c}`}>{v}</b>
            </span>
          ))}
        </div>
      </div>
      {d && !d.family_upload_question && (
        <p className="text-xs text-slate-500 dark:text-slate-400">This registration has no birth-certificate upload question — families can't send one with the form, so upload it here (or add a file question to the form).</p>
      )}
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-auto max-h-[calc(100vh-320px)] min-h-[200px] bg-white dark:bg-slate-900/40">
        {!d ? <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div> : (
          <table className="w-full text-sm" data-testid="pd-table">
            <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800 text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <tr><th className="text-left px-2 py-1.5">Player</th><th className="text-left px-2">DOB</th><th className="text-left px-2">Team</th>
                <th className="text-left px-2">Waivers</th><th className="text-left px-2">Birth certificate</th></tr>
            </thead>
            <tbody>
              {fams.map((f) => (
                <React.Fragment key={f.family + f.players[0]?.id}>
                  <tr className="bg-slate-50/70 dark:bg-slate-800/40 border-t border-slate-200 dark:border-slate-700">
                    <td colSpan={5} className="px-2 py-1 text-xs font-semibold text-slate-700 dark:text-slate-200">
                      {f.family}
                      {f.complete
                        ? <span className="ml-2 text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-0.5"><FileCheck2 className="h-3.5 w-3.5" /> complete</span>
                        : <span className="ml-2 text-amber-600 dark:text-amber-400">incomplete</span>}
                    </td>
                  </tr>
                  {f.players.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100 dark:border-slate-800 text-slate-800 dark:text-slate-200" data-testid={`pd-row-${p.id}`}>
                      <td className="px-2 py-1 pl-5">{p.name}</td>
                      <td className="px-2 text-xs">{p.dob}</td>
                      <td className="px-2 text-xs">{p.team}</td>
                      <td className="px-2">
                        <div className="flex flex-wrap gap-1">
                          {p.agreements.length === 0 && <span className="text-xs text-slate-400">no waivers on this form</span>}
                          {p.agreements.map((a) => (
                            <span key={a.id} title={a.signed ? `Signed${a.value ? `: ${a.value}` : ''}` : 'Not signed'}
                              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] ${a.signed
                                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                              {a.signed ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}{a.title}
                              {a.pdf_path && <a href={`${API_URL}${a.pdf_path}`} target="_blank" rel="noreferrer" title="The PDF version this family agreed to" className="ml-0.5 underline">PDF</a>}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-2 whitespace-nowrap">
                        {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : p.birth_certificate ? (
                          <span className="inline-flex items-center gap-1 text-xs">
                            <a href={fileUrl(p.birth_certificate.file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                              <Eye className="h-3.5 w-3.5" /> View</a>
                            <span className="text-slate-400">· {p.birth_certificate.source === 'ad' ? 'uploaded here' : 'from family'}</span>
                            {p.birth_certificate.source === 'ad' && (
                              <button type="button" onClick={() => remove(p)} title="Remove" className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                            )}
                          </span>
                        ) : (
                          <button type="button" onClick={() => pickFile(p.id)} data-testid={`pd-upload-${p.id}`}
                            className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400 hover:underline">
                            <Upload className="h-3.5 w-3.5" /> Upload
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              {!fams.length && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No registrations match.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
